import { useState, useEffect } from 'react';
import { 
  Filter, 
  Calendar, 
  Building2, 
  Tag, 
  Layers,
  Package,
  X,
  ChevronDown
} from 'lucide-react';
import DateRangePicker from './DateRangePicker';
import DepartmentFilter from './DepartmentFilter';
import ExpenseGroupFilter from './ExpenseGroupFilter';
import ExpenseTypeFilter from './ExpenseTypeFilter';
import CategoryFilter from './CategoryFilter';
import FilterService from '../../services/filterService';

function FilterPanel({ filters, onFilterChange }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (filters.dateRange?.start && filters.dateRange?.end) count++;
    if (filters.departments?.length > 0) count++;
    if (filters.expenseGroups?.length > 0) count++;
    if (filters.expenseTypes?.length > 0) count++;
    if (filters.categories?.length > 0) count++;
    setActiveFilters(count);
  }, [filters]);

  // Clean filters when dependencies change
  useEffect(() => {
    const cleanedFilters = FilterService.validateAndCleanFilters(filters);
    if (JSON.stringify(cleanedFilters) !== JSON.stringify(filters)) {
      onFilterChange(cleanedFilters);
    }
  }, [filters.departments, filters.expenseGroups, filters.expenseTypes]);

  const handleDateRangeChange = (dateRange) => {
    onFilterChange({ dateRange });
  };

  const handleDepartmentChange = (departments) => {
    onFilterChange({ departments });
  };

  const handleExpenseGroupChange = (expenseGroups) => {
    onFilterChange({ expenseGroups });
  };

  const handleExpenseTypeChange = (expenseTypes) => {
    onFilterChange({ expenseTypes });
  };

  const handleCategoryChange = (categories) => {
    onFilterChange({ categories });
  };

  const clearAllFilters = () => {
    onFilterChange({
      dateRange: { start: null, end: null },
      departments: [],
      expenseGroups: [],
      expenseTypes: [],
      categories: []
    });
  };

  const hasActiveFilters = activeFilters > 0;

  return (
    <div 
      className="rounded-xl border"
      style={{ 
        background: 'var(--panel)', 
        borderColor: 'var(--border)' 
      }}
    >
      {/* Filter Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            <h3 className="font-semibold" style={{ color: 'var(--text)' }}>
              Filters
            </h3>
          </div>
          
          {hasActiveFilters && (
            <div 
              className="px-2 py-1 rounded-full text-xs font-medium"
              style={{ 
                background: 'var(--primary)',
                color: 'white'
              }}
            >
              {activeFilters} active
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAllFilters();
              }}
              className="flex items-center gap-1 px-3 py-1 rounded-md text-sm transition-colors"
              style={{ 
                background: 'var(--border)',
                color: 'var(--text)'
              }}
            >
              <X className="w-3 h-3" />
              Clear All
            </button>
          )}
          
          <ChevronDown 
            className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            style={{ color: 'var(--muted)' }}
          />
        </div>
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="border-t p-4 space-y-6" style={{ borderColor: 'var(--border)' }}>
          {/* Date Range Filter */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <label className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Date Range
              </label>
            </div>
            <DateRangePicker
              startDate={filters.dateRange?.start}
              endDate={filters.dateRange?.end}
              onChange={handleDateRangeChange}
            />
          </div>

          {/* Department Filter */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <label className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Departments
              </label>
            </div>
            <DepartmentFilter
              selectedDepartments={filters.departments}
              onChange={handleDepartmentChange}
            />
          </div>

          {/* Expense Group Filter */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <label className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Expense Group
              </label>
            </div>
            <ExpenseGroupFilter
              selectedGroups={filters.expenseGroups}
              selectedDepartments={filters.departments}
              onChange={handleExpenseGroupChange}
            />
          </div>

          {/* Expense Type Filter */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <label className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Expense Type
              </label>
            </div>
            <ExpenseTypeFilter
              selectedTypes={filters.expenseTypes}
              selectedDepartments={filters.departments}
              selectedExpenseGroups={filters.expenseGroups}
              onChange={handleExpenseTypeChange}
            />
          </div>

          {/* Category Filter */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <label className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Categories
              </label>
            </div>
            <CategoryFilter
              selectedCategories={filters.categories}
              selectedDepartments={filters.departments}
              selectedExpenseGroups={filters.expenseGroups}
              selectedExpenseTypes={filters.expenseTypes}
              onChange={handleCategoryChange}
            />
          </div>

          {/* Quick Filter Presets */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <label className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Quick Filters
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleDateRangeChange({
                  start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                  end: new Date()
                })}
                className="px-3 py-1 rounded-md text-sm border transition-colors"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                This Month
              </button>
              <button
                onClick={() => handleDateRangeChange({
                  start: new Date(new Date().getFullYear(), 0, 1),
                  end: new Date()
                })}
                className="px-3 py-1 rounded-md text-sm border transition-colors"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                This Year
              </button>
              <button
                onClick={() => handleExpenseTypeChange(['OPEX'])}
                className="px-3 py-1 rounded-md text-sm border transition-colors"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                OPEX Only
              </button>
              <button
                onClick={() => handleExpenseTypeChange(['CAPEX'])}
                className="px-3 py-1 rounded-md text-sm border transition-colors"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                CAPEX Only
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterPanel;